/**
 * Where a default-scene crop has to be written to take effect.
 *
 * A crop (`bgX`/`bgY`/`bgScale`/`bgRotate`/`bgFlip`) describes ONE artwork, so
 * the rows that carry one are normally background-scoped — JetBrains Air has a
 * row per background (scripts/add-air-release-default-scene.mjs) and another
 * per (screenshot layout, background). The cascade merges object fields per
 * template key from least to most specific, so a crop saved at the SET level
 * lands under every one of those rows: the write succeeds, the row holds the
 * value, and the editor still shows the background row's crop. An admin who
 * dragged the artwork and pressed Save saw nothing change.
 *
 * So the level an admin picks decides the scope of everything else, and the
 * crop follows the row it currently comes from: the most specific layer of the
 * route's cascade that carries one. `shadowedCropTarget` returns that layer's
 * (layout, background) when it is more specific than the chosen level — and
 * null when the chosen row already wins, which is every channel with no
 * background-scoped crop rows at all.
 */

/** The per-template crop records a scene carries. */
export const CROP_FIELDS = ["bgX", "bgY", "bgScale", "bgRotate", "bgFlip"] as const;

export interface OverrideRow {
  channel?: string | null;
  product?: string | null;
  type?: string | null;
  set?: string | null;
  layout?: string | null;
  background?: string | null;
  variant?: string | null;
  scene_data?: Record<string, unknown> | null;
}

/**
 * The (layout, background, variant) a row is keyed by; nulls mean "the whole
 * set".
 */
export interface RowSpecificity {
  layout: string | null;
  background: string | null;
  variant: string | null;
}

export interface ShadowedCropTargetInput {
  /** Every row of the channel, as the panel already fetches them. */
  overrides: OverrideRow[] | null | undefined;
  /** The route the editor is on. */
  channel: string;
  product: string;
  type: string;
  set: string | null | undefined;
  layout: string | null | undefined;
  background: string | null | undefined;
  /** The brand treatment the route shows, where the product has one. */
  variant?: string | null;
  /** The row the admin's Scope + Level would write: "*" or a product code. */
  chosenProduct: string;
  chosenLayout: string | null | undefined;
  chosenBackground: string | null | undefined;
  chosenVariant?: string | null;
}

const scopeValue = (value: string | null | undefined): string => value || "";

/**
 * The cascade's candidate keys for one route, least → most specific.
 * Mirrors `resolveAllOverrides` (hooks/useDefaultSceneOverrides.js) and the
 * server's own list (server/api/v3/resolve-defaults.ts): every wildcard layer
 * first, then the product's own; inside a scope, background before layout,
 * then (layout, background), then the two brand-treatment layers.
 */
export function cascadeKeys(
  product: string,
  layout: string | null | undefined,
  background: string | null | undefined,
  variant: string | null | undefined
): string[] {
  const l = scopeValue(layout);
  const b = scopeValue(background);
  const v = scopeValue(variant);
  const candidates = [
    ["*", "", "", ""],
    b && ["*", "", b, ""],
    l && ["*", l, "", ""],
    l && b && ["*", l, b, ""],
    v && ["*", "", "", v],
    l && v && ["*", l, "", v],
    [product, "", "", ""],
    b && [product, "", b, ""],
    l && [product, l, "", ""],
    l && b && [product, l, b, ""],
    v && [product, "", "", v],
    l && v && [product, l, "", v],
  ].filter(Boolean) as string[][];
  // A route with no layout or no background repeats the less specific key;
  // the FIRST position is its rank.
  const keys: string[] = [];
  for (const candidate of candidates) {
    const key = candidate.join(":");
    if (!keys.includes(key)) keys.push(key);
  }
  return keys;
}

export const rowKey = (row: OverrideRow): string =>
  [
    scopeValue(row.product),
    scopeValue(row.layout),
    scopeValue(row.background),
    scopeValue(row.variant),
  ].join(":");

function hasCrop(sceneData: Record<string, unknown> | null | undefined): boolean {
  if (!sceneData) return false;
  return CROP_FIELDS.some((field) => {
    const record = sceneData[field];
    return Boolean(record) && typeof record === "object" && Object.keys(record as object).length > 0;
  });
}

/**
 * The row a crop must go to, or null when the admin's own row already wins.
 *
 * `product` is the route's product (it decides which wildcard and which
 * product rows the cascade reads); the returned specificity is meant to be
 * written under `chosenProduct`, the scope the admin picked. A more specific
 * wildcard row is therefore NOT a reason to retarget on its own: a product row
 * outranks every wildcard row whatever their layout and background.
 */
export function shadowedCropTarget({
  overrides,
  channel,
  product,
  type,
  set,
  layout,
  background,
  variant,
  chosenProduct,
  chosenLayout,
  chosenBackground,
  chosenVariant,
}: ShadowedCropTargetInput): RowSpecificity | null {
  if (!overrides?.length) return null;

  const keys = cascadeKeys(product, layout, background, variant);
  const rank = new Map(keys.map((key, index) => [key, index]));
  const chosenRank = rank.get(
    [
      chosenProduct,
      scopeValue(chosenLayout),
      scopeValue(chosenBackground),
      scopeValue(chosenVariant),
    ].join(":")
  );
  // A level outside the route's own cascade (the panel cannot produce one)
  // has nothing to compare against.
  if (chosenRank === undefined) return null;

  let winner: {row: OverrideRow; rank: number} | null = null;
  for (const row of overrides) {
    if (row.channel !== channel || row.type !== type) continue;
    if (scopeValue(row.set) !== scopeValue(set)) continue;
    if (!hasCrop(row.scene_data)) continue;
    const rowRank = rank.get(rowKey(row));
    if (rowRank === undefined) continue;
    if (!winner || rowRank > winner.rank) winner = {row, rank: rowRank};
  }

  if (!winner || winner.rank <= chosenRank) return null;
  return {
    layout: winner.row.layout || null,
    background: winner.row.background || null,
    variant: winner.row.variant || null,
  };
}

/** "bg 2", "layout 5 + bg 2" — the row a retargeted crop went to. */
export function cropTargetLabel(target: RowSpecificity): string {
  const parts: string[] = [];
  if (target.layout) parts.push(`layout ${target.layout}`);
  if (target.background) parts.push(`bg ${target.background}`);
  if (target.variant) parts.push(`variant ${target.variant}`);
  if (parts.length === 0) return "the whole set";
  return parts.join(" + ");
}

/**
 * The template keys of a `customBgs` record: everything but `main`, which is
 * the shared slot (Livestream, Ads), not a banner.
 */
export function templateImageKeys(
  customBgs: Record<string, unknown> | null | undefined
): string[] {
  if (!customBgs || typeof customBgs !== "object") return [];
  return Object.keys(customBgs).filter((key) => key !== "main" && Boolean(customBgs[key]));
}

export interface BackgroundSlotTargetInput {
  /** What shadowedCropTarget returned for the save. */
  cropTarget: RowSpecificity | null;
  /** True when the save carries a banner image (a `customBgs` template key). */
  hasTemplateImages: boolean;
  chosenBackground: string | null | undefined;
  /** The background on screen. */
  background: string;
}

/**
 * Where the background slot of a save goes: the crop, and the image of each
 * banner. A banner's image belongs to the background number it was saved
 * under — `background=N` means "the image each banner has for N" — so it is
 * written to the row of N whatever level the admin picked. The crop describes
 * that image and goes with it. A crop row more specific than the background
 * row (layout + background) still wins, as without images.
 *
 * Returns null when the slot stays on the chosen row.
 */
export function backgroundSlotTarget({
  cropTarget,
  hasTemplateImages,
  chosenBackground,
  background,
}: BackgroundSlotTargetInput): RowSpecificity | null {
  if (!hasTemplateImages || chosenBackground) return cropTarget;
  if (cropTarget?.background) return cropTarget;
  return {layout: null, background, variant: null};
}
